<template>
  <BaseForm
    id="click-filter"
    class="search-box-assessment tw-flex tw-items-center tw-rounded-[6px] tw-border tw-border-solid tw-border-[#EFF0F1] tw-bg-[#ffffff] tw-pl-[15px] tw-pt-[6px] tw-pb-[6px] tw-pr-[6px]"
  >
    <div class="tw-flex tw-flex-row tw-items-center">
      <SearchIcon :size="15" color="#B7B7B7" />
    </div>
    <div class="tw-flex tw-justify-around tw-items-center tw-w-full">
      <div class="tw-flex tw-flex-row tw-items-center tw-flex-1">
        <slot name="icon" />
        <div class="tw-flex tw-flex-row tw-items-center">
          <div
            :class="['tw-text-[#B7B7B7] tw-text-[11px] tw-p-[6px] tw-pl-[9px]']"
          >
            <span>{{
              getComponentUiText(
                "app-component-assessments-search-box-assessment-tag-2",
              )
            }}</span>
          </div>
          <div
            :class="[
              'tw-inline-flex tw-gap-[5px] tw-items-center tw-px-[5px] tw-py-[3px] tw-text-[11px] tw-text-[#387DFF] tw-bg-[#F2F8FF] tw-font-medium tw-rounded-[6px]',
            ]"
          >
            <p class="tw-m-0">
              {{
                selectedCategory && selectedCategory.text
                  ? selectedCategory.text
                  : getComponentUiText(
                      "app-component-assessments-assessment-search-modal-tag-1",
                    )
              }}
            </p>
          </div>
        </div>
        <input
          id="search-input-modal"
          ref="searchInputModal"
          v-model="valueSearch"
          type="text"
          :placeholder="hintText"
          class="input-assessment tw-relative"
          @input="debouncedSearch"
          @focus="onInputFocus(true)"
          @blur="onBlur"
          @keyup.enter="onEnter"
        />
        <div v-if="isInputFocused" class="hover-modal">
          <ModalHoverSearchAssessment
            :is-search-modal-assessment="false"
            @close-modal="onInputFocus(false)"
            @update-current-value-search="updateCurrentValueSearch"
          />
        </div>
      </div>

      <button
        v-if="modelValue !== ''"
        id="close-iten-modal"
        type="button"
        class="tw-bg-transparent tw-border-none tw-outline-none tw-cursor-pointer tw-ml-[19px]"
        @click="clearData"
      >
        <CloseBoxIcon :size="15" color="#DBDBDB" />
      </button>
    </div>
  </BaseForm>
</template>

<script setup lang="ts">
import SearchIcon from "@/components/svgs/searchIcon.vue";
import CloseBoxIcon from "@/components/svgs/closeBoxIcon.vue";
import { getComponentUiText } from "@/utils/utilities";
import BaseForm from "../common/form/baseForm.vue";
import { onUnmounted, ref, watch } from "vue";
import ModalHoverSearchAssessment from "./modalHoverSearchAssessment.vue";
import store from "@/store";
import { SEARCH_TYPE_ID } from "@/utils/constants";
import debounce from "lodash.debounce";

const emit = defineEmits(["update:modelValue", "update:searchFocus"]);
const searchInputModal = ref<HTMLInputElement | null>(null);

const props = defineProps({
  hintText: {
    type: String,
    required: true,
  },
  modelValue: {
    type: String,
    required: true,
  },
  isShowFilterAction: {
    type: Boolean,
    default: false,
  },
  selectedCategory: {
    type: Object,
    default: () => {},
  },
});

const valueSearch = ref(props.modelValue);
const lastCallApiValueSearch = ref("");
const isInputFocused = ref(false);
let valueCheckSearch = "";

watch(
  () => props.modelValue,
  debounce((newValue) => {
    valueSearch.value = newValue;
  }, 800),
  { immediate: true },
);

watch(
  () => props.isShowFilterAction,
  (newValue) => {
    if (newValue) {
      isInputFocused.value = false;
    }
  },
);

const onEnter = () => {
  isInputFocused.value = false;

  if (valueSearch.value && valueSearch.value !== lastCallApiValueSearch.value) {
    postAndGetRecentSearch();
  }
};

// const updateInput = (e: any) => {
//   emit("update:modelValue", e.target.value);
// };

const debouncedSearch = debounce(() => {
  emit("update:modelValue", valueSearch.value);
}, 800);

const isClearClicked = ref(false);

const clearData = debounce(() => {
  isClearClicked.value = true;
  valueSearch.value = "";
  emit("update:modelValue", "");
}, 500);

const updateCurrentValueSearch = (value: string) => {
  valueSearch.value = value;
  emit("update:modelValue", value);
};

const onInputFocus = (isFocus) => {
  emit("update:searchFocus", isFocus);
  valueCheckSearch = valueSearch.value;
  isInputFocused.value = isFocus;
};

const onBlur = () => {
  setTimeout(() => {
    if (valueSearch.value !== "") {
      isInputFocused.value = false;
    } else {
      if (isClearClicked.value) {
        isClearClicked.value = false;
        searchInputModal.value?.focus();
      } else {
        isInputFocused.value = false;
      }
    }
  }, 711);
  if (valueSearch.value && valueSearch.value !== lastCallApiValueSearch.value) {
    emit("update:searchFocus", false);
    postAndGetRecentSearch();
  }
};

const postAndGetRecentSearch = async () => {
  lastCallApiValueSearch.value = valueSearch.value;
  if (valueSearch.value === "" || valueCheckSearch === valueSearch.value)
    return;

  await store.dispatch("Assessment/postRecentSearchInput", {
    searchTypeId: SEARCH_TYPE_ID.CL_INPUT,
    searchData: {
      text: valueSearch.value,
    },
  });
  await store.dispatch("Assessment/getRecentSearches", {
    searchTypeId: SEARCH_TYPE_ID.CL_INPUT,
  });
};

onUnmounted(() => {
  window.removeEventListener("click", () => {});
});
</script>

<style scoped lang="scss">
.search-box-assessment {
  position: relative;
  width: 100%;
  max-width: 449px;
  outline: none;
  height: 46px;
  .input-assessment {
    border: none;
    outline: none;
    width: 100%;
    margin-left: 9px;
    font-size: 14px;
    font-weight: 500;
    line-height: normal;
    height: auto;
    padding: 0;
    color: #211f58;
    &:focus {
      border: none;
      outline: none !important;
      box-shadow: none !important;
    }

    &::placeholder {
      color: #b7b7b7;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
  }
  .search-button {
    visibility: hidden;
  }
  .search-button.hovered {
    visibility: visible;
  }
}

.hover-modal {
  position: absolute;
  z-index: 2;
  left: 0px;
  top: 46px;
  box-shadow:
    rgba(50, 50, 93, 0.25) 0px 50px 100px -20px,
    rgba(0, 0, 0, 0.3) 0px 30px 60px -30px;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  border-bottom: none;
  width: 100%;
}
</style>
